<template>
    <div class="animated fadeIn">
        <div class="kgr-row kgr-region kgr-region--lightGray kgr-region--rounded">
            <h3 class="kgr-headline kgr-headline--h3 kgr-row">
                {{ $t('Zeitraum der Rechnungsstellung') }}
            </h3>
            <div class="kgr-inputGroup">
                <div class="kgr-grid kgr-grid--gutters kgr-grid--bigGutters">
                    <div class="kgr-grid__cell">
                        <kgr-form-row
                            :label="$t('Start')"
                            :error="error"
                            field-name="start"
                        >
                            <kgr-date-picker v-model="filter.start" />
                        </kgr-form-row>
                    </div>
                    <div class="kgr-grid__cell">
                        <kgr-form-row
                            :label="$t('Ende')"
                            :error="error"
                            field-name="end"
                        >
                            <kgr-date-picker v-model="filter.end" />
                        </kgr-form-row>
                    </div>
                </div>
            </div>
        </div>
        <div class="kgr-inputGroup">
            <kgr-accordion>
                <kgr-accordion-item>
                    <template #header-left>
                        <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
                            {{ $t('Filter') }}
                        </h3>
                        <div class="kgr-text kgr-text--extraSmall">
                            {{ filterText }}
                        </div>
                    </template>
                    <div class="kgr-row">
                        <h4 class="kgr-headline kgr-headline--h4 kgr-headline--text kgr-headline--firstInText">
                            {{ $t('Rechnungsstatus') }}
                        </h4>
                        <kgr-checkbox
                            :label="$t('Offen')"
                            :checked="filter.invoiceStatus.includes('open')"
                            inline
                            @change="toggleInvoiceStatus('open')"
                        />
                        <kgr-checkbox
                            :label="$t('Bezahlt')"
                            :checked="filter.invoiceStatus.includes('paid')"
                            inline
                            @change="toggleInvoiceStatus('paid')"
                        />

                        <kgr-checkbox
                            :label="$t('Rücklastschrift')"
                            :checked="filter.invoiceStatus.includes('chargeback')"
                            inline
                            @change="toggleInvoiceStatus('chargeback')"
                        />

                        <kgr-checkbox
                            :label="$t('Überzahlung/Unterzahlung')"
                            :checked="filter.invoiceStatus.includes('unequal')"
                            inline
                            @change="toggleInvoiceStatus('unequal')"
                        />
                        <kgr-checkbox
                            :label="$t('stornierte Rechnungen')"
                            :checked="filter.others.includes('canceled')"
                            inline
                            @change="toggleOthers('canceled')"
                        />
                        <kgr-checkbox
                            :label="$t('nicht-stornierte Rechnungen')"
                            :checked="filter.others.includes('not-canceled')"
                            inline
                            @change="toggleOthers('not-canceled')"
                        />
                    </div>
                    <div class="kgr-row">
                        <h4 class="kgr-headline kgr-headline--h4 kgr-headline--text kgr-headline--firstInText">
                            {{ $t('Zahlart') }}
                        </h4>
                        <kgr-checkbox
                            :label="$t('SEPA-Lastschrift')"
                            :checked="filter.invoicePaymentMethod.includes('sepa-debit')"
                            inline
                            @change="toggleInvoicePaymentMethod('sepa-debit')"
                        />

                        <kgr-checkbox
                            :label="$t('Bar')"
                            :checked="filter.invoicePaymentMethod.includes('cash')"
                            inline
                            @change="toggleInvoicePaymentMethod('cash')"
                        />

                        <kgr-checkbox
                            :label="$t('Überweisung')"
                            :checked="filter.invoicePaymentMethod.includes('bank-transfer')"
                            inline
                            @change="toggleInvoicePaymentMethod('bank-transfer')"
                        />

                        <kgr-checkbox
                            :label="$t('Kartenzahlung')"
                            :checked="filter.invoicePaymentMethod.includes('debit-card')"
                            inline
                            @change="toggleInvoicePaymentMethod('debit-card')"
                        />

                        <kgr-checkbox
                            :label="$t('Undefiniert')"
                            :checked="filter.invoicePaymentMethod.includes('indefinite')"
                            inline
                            @change="toggleInvoicePaymentMethod('indefinite')"
                        />
                    </div>
                    <div class="kgr-row">
                        <h4 class="kgr-headline kgr-headline--h4 kgr-headline--text kgr-headline--firstInText">
                            {{ $t('Rechnungsbetrag') }}
                        </h4>
                        <kgr-checkbox
                            :label="$t('Positive Rechnungsbeträge')"
                            :checked="filter.invoiceAmount.includes('positive')"
                            inline
                            @change="toggleInvoiceAmount('positive')"
                        />
                        <kgr-checkbox
                            :label="$t('Negative Rechnungsbeträge')"
                            :checked="filter.invoiceAmount.includes('negative')"
                            inline
                            @change="toggleInvoiceAmount('negative')"
                        />
                        <kgr-checkbox
                            :label="$t('Nullbetrag-Rechnungen')"
                            :checked="filter.invoiceAmount.includes('zero')"
                            inline
                            @change="toggleInvoiceAmount('zero')"
                        />
                    </div>
                    <div class="kgr-row">
                        <h4 class="kgr-headline kgr-headline--h4 kgr-headline--text kgr-headline--firstInText">
                            {{ $t('Sonstiges') }}
                        </h4>
                        <kgr-checkbox
                            :label="$t('Rechnungen ohne Mailadresse')"
                            :checked="filter.others.includes('invoices-without-mail-address')"
                            inline
                            @change="toggleOthers('invoices-without-mail-address')"
                        />
                        <kgr-checkbox
                            :label="$t('Rechnungen ohne aktivierten Mitglieder-Login')"
                            :checked="filter.others.includes('invoices-without-active-login')"
                            inline
                            @change="toggleOthers('invoices-without-active-login')"
                        />
                        <kgr-checkbox
                            :label="$t('Rechnungen für Gäste')"
                            :checked="filter.others.includes('guest')"
                            inline
                            @change="toggleOthers('guest')"
                        />
                    </div>
                    <div class="kgr-row">
                        <h4 class="kgr-headline kgr-headline--h4 kgr-headline--text kgr-headline--firstInText">
                            {{ $t('Mahnwesen') }}
                        </h4>
                        <kgr-checkbox
                            :label="$t('Alle überfälligen Rechnungen')"
                            :checked="filter.dunningLevel.includes(0)"
                            inline
                            @change="toggleDunningLevel(0)"
                        />
                    </div>
                    <div class="kgr-row">
                        <h4 class="kgr-headline kgr-headline--h4 kgr-headline--text kgr-headline--firstInText">
                            {{ $t('Mahnstatus') }}
                        </h4>
                        <kgr-checkbox
                            :label="translateDunningFilter(0)"
                            :checked="filter.dunningStatus.includes(0)"
                            inline
                            @change="toggleDunningStatus(0)"
                        />
                        <kgr-checkbox
                            :label="translateDunningFilter(1)"
                            :checked="filter.dunningStatus.includes(1)"
                            inline
                            @change="toggleDunningStatus(1)"
                        />
                        <kgr-checkbox
                            :label="translateDunningFilter(2)"
                            :checked="filter.dunningStatus.includes(2)"
                            inline
                            @change="toggleDunningStatus(2)"
                        />
                        <kgr-checkbox
                            :label="translateDunningFilter(3)"
                            :checked="filter.dunningStatus.includes(3)"
                            inline
                            @change="toggleDunningStatus(3)"
                        />
                        <kgr-checkbox
                            :label="translateDunningFilter(4)"
                            :checked="filter.dunningStatus.includes(4)"
                            inline
                            @change="toggleDunningStatus(4)"
                        />
                    </div>
                    <div class="kgr-row">
                        <kgr-member-multi-select
                            v-model="filter.invoiceRecipients"
                            :club-id="clubId"
                            :label="$t('Rechnungsempfänger')"
                        />
                    </div>
                    <div class="kgr-row">
                        <kgr-text-input
                            v-model="filter.invoiceNumbers"
                            :label="$t('Rechnungsnummer')"
                        />
                    </div>
                </kgr-accordion-item>
            </kgr-accordion>
        </div>
        <div class="kgr-inputGroup kgr-row">
            <kgr-accordion>
                <kgr-accordion-item>
                    <template #header-left>
                        <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
                            {{ $t('Stapelverarbeitung') }}
                        </h3>
                        <div class="kgr-text kgr-text--extraSmall">
                            {{ translatedBulkOperation }}
                        </div>
                    </template>
                    <div class="kgr-row">
                        <kgr-generic-select
                            v-model="bulkOperation"
                            :options="bulkOperations"
                            display="text"
                            @post-input="resetSelectAll"
                        />
                    </div>
                </kgr-accordion-item>
            </kgr-accordion>
        </div>
        <div class="kgr-row">
            <div class="kgr-postfix">
                <div class="kgr-postfix__fluid">
                </div>
                <div class="kgr-postfix__fixed">
                    <button
                        class="kgr-button kgr-button--accent"
                        @click="clickLoadButton"
                    >
                        {{ $t('Rechnungen Anzeigen') }}
                    </button>
                </div>
            </div>
        </div>
        <hr
            v-if="showInvoices"
            class="kgr-separator--light"
        />
        <div class="kgr-grid kgr-grid--gutters kgr-grid--bigGutters">
            <div class="kgr-grid__cell">
                <kgr-invoice-open-items
                    :club-id="clubId"
                    :open-items="openItems"
                    :show="showOpenItemsAndInvoiceExport"
                    :query="query"
                    :loading="loadingOpenItems"
                />
            </div>
            <div class="kgr-grid__cell">
                <kgr-invoice-export
                    :club-id="clubId"
                    :invoice-count="totalInvoiceCount"
                    :show="showOpenItemsAndInvoiceExport"
                    :query="query"
                />
            </div>
        </div>
        <hr class="kgr-separator--light" />
        <div class="kgr-grid kgr-grid--gutters kgr-grid--bigGutters kgr-row">
            <div class="kgr-grid__cell">
                <div v-if="showInvoices && bulkOperation">
                    <div class="kgr-postfix">
                        <div class="kgr-postfix__fluid">
                            <button
                                class="kgr-button kgr-button--accent kgr-button--small"
                                :disabled="getSelectedIds.length === 0"
                                @click="executeBulkOperation()"
                            >
                                {{ translatedBulkOperation }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="kgr-grid__cell">
                <kgr-paginator
                    v-if="showPagination"
                    v-model="pagination"
                    :length="totalInvoiceCount"
                    :show-limit-options="false"
                    show-pages
                />
            </div>
        </div>
        <table class="kgr-table kgr-table--block printable">
            <thead>
                <tr class="kgr-table__row">
                    <th
                        v-if="isBulkInvoiceOperation"
                        class="kgr-table__cell kgr-table__cell--headline"
                    >
                        <kgr-checkbox
                            v-model="checkboxSelectAll"
                            @change="selectAll"
                        />
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Status') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Rechnungsnummer') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Rechnungsbetrag') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Rechnungsempfänger') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Zahlart') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Erstellt am') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions kgr-hidden-print">
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="loading">
                    <td
                        colspan="9"
                        style="text-align: center;"
                    >
                        <kgr-loader />
                    </td>
                </tr>
                <tr v-else-if="showMessageBox">
                    <td
                        colspan="9"
                        style="text-align: center;"
                    >
                        {{ messageBoxText }}
                    </td>
                </tr>
                <template v-else>
                    <template
                        v-for="(invoice, index) in invoices"
                        :key="invoice.id"
                    >
                        <tr :class="['kgr-table__row', getRowClass(index)]">
                            <td
                                v-if="isBulkInvoiceOperation"
                                class="kgr-table__cell"
                            >
                                <kgr-checkbox
                                    :checked="isSelected(invoice.id)"
                                    :inactive="isInvoiceSelectionDisabled(invoice)"
                                    :readonly="isInvoiceSelectionDisabled(invoice)"
                                    @change="toggleInvoice(invoice.id)"
                                />
                            </td>
                            <td class="kgr-table__cell kgr-table__cell--centered">
                                <kgr-invoice-icon
                                    :invoice="invoice"
                                />
                            </td>
                            <td class="kgr-table__cell">
                                <span style="color: grey">#</span>{{ invoice.number }}<template
                                    v-if="invoice.reverseInvoice"
                                >
                                    ({{ $t('storniert') }})
                                </template>
                            </td>
                            <td class="kgr-table__cell">
                                {{ overallMoney(invoice) }}
                                <kgr-invoice-open-amount :value="invoice" />
                            </td>
                            <td class="kgr-table__cell">
                                <a
                                    :href="`/backend/clubs/members/${invoice.invoiceRecipient.id}/accounting/invoicing`"
                                    class="kgr-link kgr-link--unobtrusive"
                                >

                                    {{ invoice.invoiceRecipient.name }}
                                </a>
                            </td>
                            <td class="kgr-table__cell">
                                <template v-if="invoice.isNegative()">
                                    {{ $t('Gutschrift') }}
                                </template>
                                <template v-else>
                                    {{ translatePaymentMethod(invoice.paymentMethod) }}
                                </template>
                                <kgr-sepa-label :invoice="invoice" />
                            </td>
                            <td class="kgr-table__cell">
                                {{ formatDate(invoice.createdAt) }}
                                <template v-if="invoice.canceled">
                                    ({{ $t('storniert am') }} {{ formatDate(invoice.reverseInvoice.createdAt) }})
                                </template>
                                <template
                                    v-if="invoice.dunningStatus.status === 0 && null !== invoice.dunningStatusMessage"
                                >
                                    ({{ invoice.dunningStatusMessage }})
                                </template>
                            </td>
                            <td class="kgr-table__cell kgr-table__cell--actions kgr-hidden-print">
                                <span class="kgr-float kgr-float--right">
                                    <kgr-invoice-edit-buttons
                                        :value="invoice"
                                        :club-dunning-settings="clubDunningSettings"
                                        @invoiceReload="reloadInvoiceAndOpenItems(index)"
                                        @invoiceReloadWithPolling="reloadInvoiceAndOpenItemsAndSchedulePolling(invoice, index)"
                                    />
                                    <kgr-invoice-dunning-button
                                        :value="invoice"
                                        :dunning-disabled="dunningDisabled"
                                        :dunning-checker="dunningChecker"
                                        @dunningLevelChanged="(parameters) => saveDunningLevel(index, parameters)"
                                    />
                                    <kgr-invoice-download-buttons :invoice="invoice" />
                                    <kgr-invoice-email-button
                                        :invoice="invoice"
                                        @sendMail="openEmailHintDialog(invoice, index)"
                                    />
                                    <button
                                        class="kgr-table__action"
                                        :disabled="!invoice.deletable"
                                        @click="deleteInvoice(invoice.id)"
                                    >
                                        <kgr-icon
                                            :title="getDeleteInvoiceButtonTitle(invoice)"
                                            icon-class="delete"
                                            :disabled="!invoice.deletable"
                                        />
                                    </button>
                                    <button
                                        class="kgr-table__action"
                                        :disabled="invoice.isReverseInvoice || invoice.canceled"
                                        @click="cancelInvoice(invoice.id, index)"
                                    >
                                        <kgr-icon
                                            :disabled="invoice.isReverseInvoice || invoice.canceled"
                                            icon-class="clear"
                                            small
                                            :title="$t('Rechnung stornieren')"
                                        />
                                    </button>
                                </span>
                            </td>
                        </tr>
                        <tr
                            v-if="invoice.reverseInvoice"
                            :class="['kgr-table__row', getRowClass(index)]"
                        >
                            <td
                                v-if="isBulkInvoiceOperation"
                                class="kgr-table__cell"
                            >
                                <kgr-checkbox
                                    :checked="isSelected(invoice.reverseInvoice.id)"
                                    :inactive="isInvoiceSelectionDisabled(invoice.reverseInvoice)"
                                    :readonly="isInvoiceSelectionDisabled(invoice.reverseInvoice)"
                                    @change="toggleInvoice(invoice.reverseInvoice.id)"
                                />
                            </td>
                            <td class="kgr-table__cell">
                            </td>
                            <td class="kgr-table__cell">
                                <kgr-icon
                                    style="margin-bottom: -5px; margin-left: 15px;"
                                    icon-class="arrow-forward"
                                    disabled
                                    small
                                />
                                {{ $t('Stornorechnung') }} <span style="color: grey">#</span>{{ invoice.reverseInvoice.number }}
                                <span v-if="invoice.isNegative()">
                                    , {{ $t('Zahlart') }}: {{ translatePaymentMethod(invoice.reverseInvoice.paymentMethod) }}
                                </span>
                            </td>
                            <td class="kgr-table__cell">
                            </td>
                            <td class="kgr-table__cell">
                            </td>
                            <td class="kgr-table__cell">
                            </td>
                            <td class="kgr-table__cell">
                            </td>
                            <td
                                class="kgr-table__cell kgr-table__cell--actions kgr-hidden-print"
                            >
                                <span class="kgr-float kgr-float--right">
                                    <kgr-invoice-download-buttons :invoice="invoice.reverseInvoice" />
                                    <kgr-invoice-email-button
                                        :invoice="invoice.reverseInvoice"
                                        @sendMail="openEmailHintDialog(invoice.reverseInvoice, index)"
                                    />
                                    <button
                                        class="kgr-table__action"
                                        :disabled="!invoice.reverseInvoice.deletable"
                                        @click="deleteReverseInvoice(invoice.reverseInvoice.id)"
                                    >
                                        <kgr-icon
                                            :title="getDeleteInvoiceButtonTitle(invoice.reverseInvoice)"
                                            icon-class="delete"
                                            :disabled="!invoice.reverseInvoice.deletable"
                                        />
                                    </button>
                                    <kgr-icon
                                        icon-class="clear"
                                        class="kgr-table__action"
                                        disabled
                                        small
                                        style="visibility: hidden"
                                    />

                                </span>
                            </td>
                        </tr>
                        <kgr-invoice-chargeback
                            v-if="invoice.chargeback !== null"
                            :invoice="invoice"
                            :class="['kgr-table__row', getRowClass(index)]"
                            :is-bulk-invoice-operation="isBulkInvoiceOperation"
                            @reloadInvoice="reloadInvoiceAndOpenItems(index)"
                            @reloadInvoiceWithPolling="reloadInvoiceAndOpenItemsAndSchedulePolling(invoice, index)"
                            @sendChargeback="sendChargebackMail(invoice, index)"
                        />
                        <kgr-invoice-dunning-level
                            v-if="invoice.dunningStatus.status >= dunningLevel1 && clubDunningSettings.clubDunningLevel1.enabled"
                            :invoice="invoice"
                            :class="['kgr-table__row', getRowClass(index)]"
                            :dunning-level="dunningLevel1"
                            :label="translateDunningFilter(dunningLevel1)"
                            :is-bulk-invoice-operation="isBulkInvoiceOperation"
                            @reloadInvoice="reloadInvoice(index)"
                            @sendDunning="sendDunningMail(invoice, index, dunningLevel1)"
                        />
                        <kgr-invoice-dunning-level
                            v-if="invoice.dunningStatus.status >= dunningLevel2"
                            :invoice="invoice"
                            :class="['kgr-table__row', getRowClass(index)]"
                            :dunning-level="dunningLevel2"
                            :label="translateDunningFilter(dunningLevel2)"
                            :is-bulk-invoice-operation="isBulkInvoiceOperation"
                            @reloadInvoice="reloadInvoice(index)"
                            @sendDunning="sendDunningMail(invoice, index, dunningLevel2)"
                        />
                        <kgr-invoice-dunning-level
                            v-if="invoice.dunningStatus.status >= dunningLevel3"
                            :invoice="invoice"
                            :class="['kgr-table__row', getRowClass(index)]"
                            :dunning-level="dunningLevel3"
                            :label="translateDunningFilter(dunningLevel3)"
                            :is-bulk-invoice-operation="isBulkInvoiceOperation"
                            @reloadInvoice="reloadInvoice(index)"
                            @sendDunning="sendDunningMail(invoice, index, dunningLevel3)"
                        />
                        <kgr-invoice-dunning-level
                            v-if="invoice.dunningStatus.status >= dunningLevel4"
                            :invoice="invoice"
                            :class="['kgr-table__row', getRowClass(index)]"
                            :dunning-level="dunningLevel4"
                            :label="translateDunningFilter(dunningLevel4)"
                            :is-bulk-invoice-operation="isBulkInvoiceOperation"
                            @reloadInvoice="reloadInvoice(index)"
                            @sendDunning="sendDunningMail(invoice, index, dunningLevel4)"
                        />
                    </template>
                </template>
            </tbody>
        </table>
        <div class="kgr-row"></div>
        <div class="kgr-grid kgr-grid--gutters kgr-grid--bigGutters">
            <div class="kgr-grid__cell">
                <div v-if="showInvoices && bulkOperation">
                    <div class="kgr-postfix">
                        <div class="kgr-postfix__fluid">
                            <button
                                class="kgr-button kgr-button--accent kgr-button--small"
                                :disabled="getSelectedIds.length === 0"
                                @click="executeBulkOperation()"
                            >
                                {{ translatedBulkOperation }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="kgr-grid__cell">
                <kgr-paginator
                    v-if="showPagination"
                    v-model="pagination"
                    :length="totalInvoiceCount"
                    :show-limit-options="false"
                    show-pages
                />
            </div>
        </div>
        <kgr-invoice-file-count-dialog
            :show="showFileCountDialog"
            :selected-count="getSelectedIds.length"
            @close="closeFileCountDialog"
        />
        <kgr-invoice-email-dialog
            :show="showEmailHintDialog"
            :invoice="invoiceSelectedForEmail"
            @close="closeEmailHintDialog"
            @confirm="sendMail(invoiceSelectedForEmail, invoiceIndexSelectedForEmail)"
        />
        <kgr-invoice-dunning-email-dialog
            :show="showDunningEmailHintDialog"
            :invoice="invoiceSelectedForDunningEmail"
            :dunning-level="dunningLevelSelectedForEmail"
            :dunning-level-label="translateDunningFilter(dunningLevelSelectedForEmail)"
            @close="closeDunningEmailHintDialog"
            @confirm="confirmSendDunningMail"
        />
        <kgr-invoice-bulk-email-dialog
            :show="showBulkEmailHintDialog"
            :invoices="bulkInvoicesSelected"
            @close="closeBulkEmailsHintDialog"
            @confirm="bulkSendInvoiceAsEmail"
        />
        <kgr-invoice-dunning-bulk-email-dialog
            :show="showBulkDunningEmailHintDialog"
            :invoices="bulkInvoicesSelected"
            @close="closeBulkDunningEmailsHintDialog"
            @confirm="confirmBulkSendDunningPdfsAsEmail"
        />
        <kgr-invoice-mark-paid-dialog
            :show="showMarkPaidDialog"
            :applied-at="bulkAppliedAt"
            :error="error"
            @close="closeMarkPaidDialog"
            @confirm="bulkMarkAs('paid')"
            @update:applied-at="bulkAppliedAt = $event"
        />
    </div>
</template>

<script>
import { endOfToday, format, isAfter, parse, subDays } from '@legacy/js/date/date-fns.js';
import Accordion from '../../../components/common/accordion/Accordion.vue';
import AccordionItem from '../../../components/common/accordion/AccordionItem.vue';
import Checkbox from '../../../components/common/form/checkbox/Checkbox.vue'
import ClubDunningSettings from '../../../js/dto/ClubDunningSettings.js';
import ClubDunningSettingsRepository from '../../../js/repository/ClubDunningSettingsRepository.js';
import ClubInvoice from '../../../js/dto/ClubInvoice.js';
import ClubInvoiceChargebackPdfRepository from '../../../js/repository/ClubInvoiceChargebackPdfRepository.js';
import ClubInvoiceDunningPdfRepository from '../../../js/repository/ClubInvoiceDunningPdfRepository.js';
import ClubInvoicePdfRepository from '../../../js/repository/ClubInvoicePdfRepository.js';
import ClubInvoiceRepository from '../../../js/repository/ClubInvoiceRepository.js';
import ClubMemberMultiSelect from '../../../components/common/form/select/ClubMemberMultiSelect.vue';
import ClubReverseInvoiceRepository from '../../../js/repository/ClubReverseInvoiceRepository.js';
import DatePicker from '../../../components/common/form/picker/DatePicker.vue';
import DunningChecker from '../../../js/accounting/DunningChecker.js';
import Error from '../../../js/model/Error.js';
import FormRow from '../../../components/common/form/FormRow.vue';
import GenericSelect from '../../../components/common/form/select/GenericSelect.vue'
import Helper from '../../../js/accounting/Helper.js';
import Icon from '../../../components/common/icon/Icon.vue';
import InvoiceBulkEmailDialog from '../../../components/club/accounting/InvoiceBulkEmailDialog.vue';
import InvoiceChargeback from '../../../components/club/accounting/InvoiceChargeback.vue';
import InvoiceDownloadButtons from '../../../components/club/accounting/InvoiceDownloadButtons.vue';
import InvoiceDunningBulkEmailDialog from '../../../components/club/accounting/InvoiceDunningBulkEmailDialog.vue';
import InvoiceDunningButton from '../../../components/club/accounting/InvoiceDunningButton.vue';
import InvoiceDunningEmailDialog from '../../../components/club/accounting/InvoiceDunningEmailDialog.vue';
import InvoiceDunningLevel from '../../../components/club/accounting/InvoiceDunningLevel.vue';
import InvoiceEditButtons from '../../../components/club/accounting/InvoiceEditButtons.vue';
import InvoiceEmailButton from '../../../components/club/accounting/InvoiceEmailButton.vue';
import InvoiceEmailDialog from '../../../components/club/accounting/InvoiceEmailDialog.vue';
import InvoiceExport from '../../../components/club/accounting/InvoiceExport.vue';
import InvoiceFileCountDialog from '../../../components/club/accounting/InvoiceFileCountDialog.vue';
import InvoiceIcon from '../../../components/club/accounting/InvoiceIcon.vue';
import InvoiceMarkPaidDialog from '../../../components/club/accounting/InvoiceMarkPaidDialog.vue';
import InvoiceOpenAmount from '../../../components/club/accounting/InvoiceOpenAmount.vue';
import InvoiceOpenItems from '../../../components/club/accounting/InvoiceOpenItems.vue';
import Loading from '../../../components/common/Loading.vue';
import OpenItems from '../../../js/dto/OpenItems.js';
import Paginator from '../../../components/common/Paginator.vue';
import SepaLabel from '../../../components/club/accounting/SepaLabel.vue';
import TextInput from '../../../components/common/form/input/TextInput.vue'
import Vue from 'vue';
import axios from 'axios';

const FILTER_OTHERS_CANCELED = 'canceled';
const FILTER_OTHERS_NOT_CANCELED = 'not-canceled';
const FILTER_OTHERS_WITHOUT_MAIL_ADDRESS = 'invoices-without-mail-address';
const FILTER_OTHERS_WITHOUT_ACTIVE_LOGIN = 'invoices-without-active-login';
const FILTER_OTHERS_GUEST = 'guest';

const BULK_DELETE = 'delete';
const BULK_DELETE_REVERSE = 'deleteReverse';
const BULK_MARK_PAID = 'markPaid';
const BULK_CANCEL_INVOICE = 'cancelInvoice';
const BULK_DOWNLOAD_INVOICES = 'downloadInvoices';
const BULK_SEND_INVOICE_AS_MAIL = 'sendInvoiceAsEmail';
const BULK_DOWNLOAD_COLLECTED_INVOICES = 'downloadCollectedInvoices';
const BULK_SEND_DUNNING_PDFS_AS_MAIL = 'sendDunningPdfsAsEmail';
const BULK_DOWNLOAD_DUNNING_PDFS = 'downloadDunningPdfs';
const BULK_DOWNLOAD_COLLECTED_DUNNING_PDFS = 'downloadCollectedDunningPdfs';
const BULK_INCREASE_DUNNING_STATUS = 'increaseDunningStatus';
const BULK_SEND_CHARGEBACK_PDFS_AS_MAIL = 'sendChargebackPdfsAsEmail';
const BULK_DOWNLOAD_CHARGEBACK_PDFS = 'downloadChargebackPdfs';
const BULK_DOWNLOAD_COLLECTED_CHARGEBACK_PDFS = 'downloadCollectedChargebackPdfs';

const POLLING_INTERVAL_IN_SECONDS = 7;
const MAX_POLLING = 3;
const DEFAULT_POLLING_INDEX = -1;

export default {
    components: {
        'kgr-accordion': Accordion,
        'kgr-accordion-item': AccordionItem,
        'kgr-checkbox': Checkbox,
        'kgr-paginator': Paginator,
        'kgr-date-picker': DatePicker,
        'kgr-invoice-icon': InvoiceIcon,
        'kgr-generic-select': GenericSelect,
        'kgr-icon': Icon,
        'kgr-form-row': FormRow,
        'kgr-loader': Loading,
        'kgr-invoice-open-items': InvoiceOpenItems,
        'kgr-invoice-export': InvoiceExport,
        'kgr-text-input': TextInput,
        'kgr-sepa-label': SepaLabel,
        'kgr-member-multi-select': ClubMemberMultiSelect,
        'kgr-invoice-email-button': InvoiceEmailButton,
        'kgr-invoice-edit-buttons': InvoiceEditButtons,
        'kgr-invoice-open-amount': InvoiceOpenAmount,
        'kgr-invoice-download-buttons': InvoiceDownloadButtons,
        'kgr-invoice-dunning-button': InvoiceDunningButton,
        'kgr-invoice-dunning-level': InvoiceDunningLevel,
        'kgr-invoice-chargeback': InvoiceChargeback,
        'kgr-invoice-file-count-dialog': InvoiceFileCountDialog,
        'kgr-invoice-email-dialog': InvoiceEmailDialog,
        'kgr-invoice-dunning-email-dialog': InvoiceDunningEmailDialog,
        'kgr-invoice-dunning-bulk-email-dialog': InvoiceDunningBulkEmailDialog,
        'kgr-invoice-bulk-email-dialog': InvoiceBulkEmailDialog,
        'kgr-invoice-mark-paid-dialog': InvoiceMarkPaidDialog,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            error: new Error(),
            showMarkPaidDialog: false,
            showFileCountDialog: false,
            showEmailHintDialog: false,
            invoiceSelectedForEmail: null,
            invoiceIndexSelectedForEmail: null,
            showDunningEmailHintDialog: false,
            invoiceSelectedForDunningEmail: null,
            invoiceIndexSelectedForDunningEmail: null,
            dunningLevelSelectedForEmail: null,
            showBulkEmailHintDialog: false,
            bulkInvoicesSelected: [],
            bulkInvoicesIndexSelected: [],
            showBulkDunningEmailHintDialog: false,
            dunningChecker: new DunningChecker(),
            helper: new Helper(),
            bulkAppliedAt: null,
            loading: false,
            loadingOpenItems: false,
            invoices: [],
            alreadyLoaded: false,
            showDunningDialog: false,
            dunningDialogInvoiceId: null,
            dunningDialogDunningLevel: null,
            dunningDialogIndex: null,
            filter: {
                invoiceStatus: [],
                invoicePaymentMethod: [],
                invoiceAmount: [],
                invoiceNumbers: '',
                others: [],
                invoiceRecipients: [],
                dunningStatus: [],
                dunningLevel: [],
                start: format(subDays(new Date(), 7), 'dd.MM.yyyy'),
                end: format(new Date(), 'dd.MM.yyyy'),
            },
            lastFilter: '',
            members: null,
            bulkOperation: null,
            bulkOperations: [
                { value: BULK_DELETE, text: this.$t('Löschen'), target: 'invoice' },
                { value: BULK_DELETE_REVERSE, text: this.$t('Stornorechnungen Löschen'), target: 'invoice' },
                { value: BULK_MARK_PAID, text: this.$t('Als bezahlt markieren'), target: 'invoice' },
                { value: BULK_CANCEL_INVOICE, text: this.$t('Rechnungen stornieren'), target: 'invoice' },
                { value: BULK_DOWNLOAD_INVOICES, text: this.$t('Rechnungen herunterladen (max. 100 pro Download)'), target: 'invoice' },
                { value: BULK_DOWNLOAD_COLLECTED_INVOICES, text: this.$t('Rechnungen gesammelt in einer PDF-Datei herunterladen (max. 100 pro Download)'), target: 'invoice' },
                { value: BULK_SEND_INVOICE_AS_MAIL, text: this.$t('Rechnungen per E-Mail versenden'), target: 'invoice' },
                { value: BULK_INCREASE_DUNNING_STATUS, text: this.$t('Auf nächste Mahnstufe setzen'), target: 'invoice' },
                { value: BULK_SEND_DUNNING_PDFS_AS_MAIL, text: this.$t('Mahnungen per E-Mail versenden'), target: 'invoice' },
                { value: BULK_DOWNLOAD_DUNNING_PDFS, text: this.$t('Aktuelle Mahnungen herunterladen (max. 100 pro Download)'), target: 'invoice' },
                { value: BULK_DOWNLOAD_COLLECTED_DUNNING_PDFS, text: this.$t('Aktuelle Mahnungen in einer PDF-Datei herunterladen (max. 100 pro Download)'), target: 'invoice' },
                { value: BULK_SEND_CHARGEBACK_PDFS_AS_MAIL, text: this.$t('Rücklastschrift-Informationen per E-Mail versenden'), target: 'invoice' },
                { value: BULK_DOWNLOAD_CHARGEBACK_PDFS, text: this.$t('Rücklastschrift-Informationen herunterladen (max. 100 pro Download)'), target: 'invoice' },
                { value: BULK_DOWNLOAD_COLLECTED_CHARGEBACK_PDFS, text: this.$t('Rücklastschrift-Informationen in einer PDF-Datei herunterladen (max. 100 pro Download)'), target: 'invoice' },
            ],
            bulkSelection: [],
            pagination: {
                offset: 0,
                limit: 100,
            },
            totalInvoiceCount: 0,
            pageCount: 0,
            dunningLevel1: ClubDunningSettings.LEVEL1,
            dunningLevel2: ClubDunningSettings.LEVEL2,
            dunningLevel3: ClubDunningSettings.LEVEL3,
            dunningLevel4: ClubDunningSettings.LEVEL4,
            loadButtonDisabled: false,
            checkboxSelectAll: false,
            clubDunningSettings: new ClubDunningSettings(),
            openItems: new OpenItems(),
            reloadInvoiceJson: [],
            reloadInvoiceCount: [],
        }
    },
    computed: {
        otherFilters() {
            if(this.filter.others.includes(FILTER_OTHERS_CANCELED) && this.filter.others.includes(FILTER_OTHERS_NOT_CANCELED)) {
                return this.filter.others.filter(other => other !== FILTER_OTHERS_CANCELED && other !== FILTER_OTHERS_NOT_CANCELED);
            }
            return this.filter.others;
        },
        invoiceNumberQuery() {
            if (this.filter.invoiceNumbers === null || this.filter.invoiceNumbers === '') {
                return [];
            }
            return this.filter.invoiceNumbers.split(' ');
        },
        isBulkInvoiceOperation() {
            let bulkOperation = this.bulkOperations.find(bulkOperation => bulkOperation.value === this.bulkOperation?.value);
            return bulkOperation && bulkOperation.target === 'invoice';
        },
        dunningDialogText() {
            if (this.dunningDialogDunningLevel !== null) {
                const invoice = this.invoices[this.dunningDialogIndex];
                const invoiceRecipient = invoice.invoiceRecipient !== undefined ? invoice.invoiceRecipient.name : '';
                return this.helper.getDunningDialogText(
                    this.dunningDialogDunningLevel,
                    invoiceRecipient,
                    invoice.dunningStatus.status
                );
            }
            return '';
        },
        isFilterEmpty() {
            return this.filter.invoiceStatus.length === 0 &&
                this.filter.invoicePaymentMethod.length === 0 &&
                this.filter.invoiceAmount.length === 0 &&
                this.invoiceNumberQuery.length === 0 &&
                this.otherFilters.length === 0 &&
                this.filter.invoiceRecipients.length === 0 &&
                this.filter.dunningLevel.length === 0 &&
                this.filter.dunningStatus.length === 0;
        },
        filterText() {
            if (this.isFilterEmpty) {
                return this.$t('keine Filter');
            }

            let filterText = '';

            if (this.filter.invoiceStatus.length > 0) {
                filterText += this.$t('Nur mit dem Status') + ' ' + this.filter.invoiceStatus.map(status => '"' + this.helper.translateInvoiceStatus(status) + '"').join(', ');
            }

            if (this.filter.invoicePaymentMethod.length > 0) {
                if (filterText === '') {
                    filterText += this.$t('Nur mit der Zahlart') + ' ';
                } else {
                    filterText += ' ' + this.$t('und nur mit der Zahlart') + ' ';
                }
                filterText += this.filter.invoicePaymentMethod.map(paymentMethod => '"' + this.translatePaymentMethod(paymentMethod) + '"').join(', ');
            }

            if (this.filter.invoiceAmount.length > 0) {
                if (filterText === '') {
                    filterText += this.$t('Begrenzt auf die Rechnungsbeträge') + ' ';
                } else {
                    filterText += ' ' + this.$t('und begrenzt auf die Rechnungsbeträge') + ' ';
                }
                filterText += this.filter.invoiceAmount.map(invoiceAmount => '"' + this.translateInvoiceAmount(invoiceAmount) + '"').join(', ');
            }

            if (this.invoiceNumberQuery.length > 0) {
                if (filterText === '') {
                    filterText += this.$t('Suche nach') + ' ';
                } else {
                    filterText += ' ' + this.$t('und Suche nach') + ' ';
                }
                filterText += this.$t('Rechnungsnummer') + ': ' + this.invoiceNumberQuery.join(' ');
            }

            if (this.filter.dunningLevel.length > 0) {
                if (filterText === '') {
                    filterText += this.$t('Nur überfällige Rechnungen') + ' ';
                } else {
                    filterText += ' ' + this.$t('und überfällige Rechnungen') + ' ';
                }
            }

            if (this.filter.dunningStatus.length > 0) {
                if (filterText === '') {
                    filterText += this.$t('Nur mit dem Mahnstatus') + ' ';
                } else {
                    filterText += ' ' + this.$t('und dem Mahnstatus') + ' ';
                }
                filterText += this.filter.dunningStatus.map(status => '"' + this.$t(this.helper.translateDunningStatus(status)) + '"').join(', ');
            }

            if (this.filter.invoiceRecipients.length > 0) {
                if (filterText === '') {
                    filterText += this.$t('Begrenzt auf') + ' ';
                } else {
                    filterText += ' ' + this.$t('und begrenzt auf') + ' ';
                }
                filterText += this.filter.invoiceRecipients.length + ' ' + this.$t('Rechnungsempfänger');
            }

            if (this.filter.others.includes(FILTER_OTHERS_CANCELED)) {
                filterText += filterText === ''
                    ? this.$t('Begrenzt auf stornierte Rechnungen')
                    : ' ' + this.$t('und begrenzt auf stornierte Rechnungen');
            }
            if (this.filter.others.includes(FILTER_OTHERS_NOT_CANCELED)) {
                filterText += filterText === ''
                    ? this.$t('Begrenzt auf nicht-stornierte Rechnungen')
                    : ' ' + this.$t('und begrenzt auf nicht-stornierte Rechnungen');
            }
            if (this.filter.others.includes(FILTER_OTHERS_WITHOUT_MAIL_ADDRESS)) {
                filterText += filterText === ''
                    ? this.$t('Begrenzt auf Rechnungen ohne Mailadresse')
                    : ' ' + this.$t('und begrenzt auf Rechnungen ohne Mailadresse');
            }
            if (this.filter.others.includes(FILTER_OTHERS_WITHOUT_ACTIVE_LOGIN)) {
                filterText += filterText === ''
                    ? this.$t('Begrenzt auf Rechnungen von Mitgliedern ohne aktiven Login')
                    : ' ' + this.$t('und begrenzt auf Rechnungen von Mitgliedern ohne aktiven Login');
            }
            if (this.filter.others.includes(FILTER_OTHERS_GUEST)) {
                filterText += filterText === ''
                    ? this.$t('Begrenzt auf Rechnungen für Gäste')
                    : ' ' + this.$t('und begrenzt auf Rechnungen für Gäste');
            }

            return filterText;
        },
        translatedBulkOperation() {
            switch (this.bulkOperation?.value) {
                case BULK_DELETE:
                    return this.$t('Löschen');
                case BULK_DELETE_REVERSE:
                    return this.$t('Stornorechnungen Löschen');
                case BULK_MARK_PAID:
                    return this.$t('Als bezahlt markieren');
                case BULK_CANCEL_INVOICE:
                    return this.$t('Rechnungen stornieren');
                case BULK_DOWNLOAD_INVOICES:
                    return this.$t('Rechnungen herunterladen');
                case BULK_DOWNLOAD_COLLECTED_INVOICES:
                    return this.$t('Rechnungen gesammelt herunterladen');
                case BULK_SEND_INVOICE_AS_MAIL:
                    return this.$t('Rechnungen per E-Mail versenden');
                case BULK_INCREASE_DUNNING_STATUS:
                    return this.$t('Auf nächste Mahnstufe setzen');
                case BULK_SEND_DUNNING_PDFS_AS_MAIL:
                    return this.$t('Mahnungen per E-Mail versenden');
                case BULK_DOWNLOAD_DUNNING_PDFS:
                    return this.$t('Aktuelle Mahnungen herunterladen (max. 100 pro Download)');
                case BULK_DOWNLOAD_COLLECTED_DUNNING_PDFS:
                    return this.$t('Aktuelle Mahnungen gesammelt in einer PDF-Datei herunterladen (max. 100 pro Download)');
                case BULK_SEND_CHARGEBACK_PDFS_AS_MAIL:
                    return this.$t('Rücklastschrift-Informationen per E-Mail versenden');
                case BULK_DOWNLOAD_CHARGEBACK_PDFS:
                    return this.$t('Rücklastschrift-Informationen herunterladen (max. 100 pro Download)');
                case BULK_DOWNLOAD_COLLECTED_CHARGEBACK_PDFS:
                    return this.$t('Rücklastschrift-Informationen gesammelt in einer PDF-Datei herunterladen (max. 100 pro Download)');
            }
            return '-';
        },
        showMessageBox() {
            return !this.loading && this.invoices.length === 0 || this.filterChanged;
        },
        messageBoxText() {
            if (this.filterChanged && this.alreadyLoaded) {
                return this.$t('Deine Filter-Einstellung hat sich geändert. Bitte klick erneut auf "Rechnungen anzeigen".');
            } else if (this.alreadyLoaded) {
                return this.$t('Es existieren derzeit keine Rechnungen, die den Filtereinstellungen entsprechen.');
            }
            return this.$t('Bitte klick auf "Rechnungen anzeigen".');
        },
        showInvoices() {
            return !this.loading && !this.filterChanged;
        },
        showPagination() {
            return this.showInvoices && (this.pageCount > 1 || this.pagination.offset > 0);
        },
        showOpenItemsAndInvoiceExport() {
            return this.invoices.length > 0 && this.showInvoices;
        },
        dunningDisabled() {
            return this.dunningChecker.isDunningDisabled(this.clubDunningSettings)
        },
        formattedStart() {
            try {
                const parsed = parse(this.filter.start, 'dd.MM.yyyy', new Date());
                if (!(parsed instanceof Date) || isNaN(parsed)) {
                    return ''
                }

                return format(parsed, 'yyyy-MM-dd');
            } catch (error) {
                console.error('Error parsing start date:', error);
                return '';
            }
        },
        formattedEnd() {
            try {
                const parsed = parse(this.filter.end, 'dd.MM.yyyy', new Date());
                if (!(parsed instanceof Date) || isNaN(parsed)) {
                    return ''
                }

                return format(parsed, 'yyyy-MM-dd');
            } catch (error) {
                console.error('Error parsing end date:', error);
                return '';
            }
        },
        query() {
            let query = [
                'dateRange=' + this.formattedStart + ':' + this.formattedEnd,
                'limit=' + this.pagination.limit,
                'offset=' + this.pagination.offset,
            ];
            let params = {
                'invoiceStatus': this.filter.invoiceStatus,
                'invoicePaymentMethods': this.filter.invoicePaymentMethod,
                'invoiceRecipients': this.filter.invoiceRecipients.map((recipient) => recipient.id),
                'invoiceAmounts': this.filter.invoiceAmount,
                'others': this.otherFilters,
                'invoiceNumbers': this.invoiceNumberQuery,
                'dunningStatus': this.filter.dunningStatus,
                'dunningLevel': this.filter.dunningLevel,
            };
            Object.keys(params).map(key => params[key].forEach(value => query.push(key + '[]=' + value)));
            return query.length === 0 ? '' : '?' + query.join('&');
        },
        todayAsString() {
            return format(new Date(), 'dd.MM.yyyy');
        },
        getSelectedReverseInvoiceIds() {
            const selectedIds = this.getSelectedIds;
            return this.invoices.filter(invoice => {
                if (!invoice.reverseInvoice) {
                    return false;
                }
                return selectedIds.includes(invoice.reverseInvoice.id)
            }).map(invoice => invoice.reverseInvoice.id);
        },
        getSelectedIds() {
            return this.bulkSelection;
        },
        filterChanged() {
            return this.lastFilter !== JSON.stringify(this.filter);
        },
    },
    watch: {
        'pagination.offset': function () {
            this.resetSelectAll();
            this.loadInvoices();
        },
    },
    created() {
        this.loadClubDunningSettings();
        this.bulkAppliedAt = this.todayAsString;

    },
    methods: {
        deleteInvoice(id) {
            showConfirm(this.$t('confirmInformal'), this.$t('Willst du die Rechnung wirklich löschen?'), () => {
                this.loading = true;
                ClubInvoiceRepository.deleteByInvoiceId(id)
                    .then(() => this.reloadInvoicesAndOpenItems())
                    .catch(() => this.$notify.error('saveChanges.error'))
                    .finally(() => this.loading = false);
            }, this.$t('Löschen'));
        },
        deleteReverseInvoice(id) {
            showConfirm(this.$t('confirmInformal'), this.$t('Willst du die Rechnung wirklich löschen?'), () => {
                this.loading = true;
                ClubReverseInvoiceRepository.deleteByInvoiceId(id)
                    .then(() => {
                        this.$notify.success('saveChanges.success');
                        this.reloadInvoicesAndOpenItems();
                    })
                    .catch(() => this.loading = false);
            }, this.$t('Löschen'));
        },
        cancelInvoice(invoiceId, index) {
            showConfirm(this.$t('confirmInformal'), this.$t('Willst du die Rechnung wirklich stornieren?'), () => {
                ClubInvoiceRepository.cancelByClubIdAndInvoiceIds(this.clubId, [invoiceId])
                    .then(() => {
                        this.reloadInvoiceAndOpenItems(index);
                        this.scheduleSinglePolling(invoiceId, index);
                    })
                    .catch(() => this.$notify.error('saveChanges.error'))
                    .finally(() => this.loading = false);
            }, this.$t('Stornieren'));
        },
        clickLoadButton() {
            this.pagination.offset = 0;
            this.reloadInvoicesAndOpenItems();
        },
        closeDunningDialog() {
            this.showDunningDialog = false;
        },
        saveDunningLevel(index, parameters) {
            const invoice = this.invoices[index];
            this.dunningDialogInvoiceId = parameters.invoiceId;
            this.dunningDialogDunningLevel = parameters.dunningLevel;
            this.dunningDialogIndex = index;
            const poll = this.dunningDialogDunningLevel > invoice.dunningStatus.status;
            showConfirm(this.$t('confirmInformal'), this.dunningDialogText, () => {

                ClubInvoiceRepository.updateDunningStatus(this.dunningDialogInvoiceId, this.dunningDialogDunningLevel)
                    .then(() => {
                        this.reloadInvoiceAndOpenItems(index);
                        if (poll) {
                            this.scheduleSinglePolling(invoice.id, index);
                        }
                    })
                    .catch(() => this.$notify.error('saveChanges.error'));
            }, this.$t('Ja'));
        },
        loadInvoices(setJson = false) {
            if (this.formattedEnd < this.formattedStart) {
                this.error.add('start', this.$t('Das Enddatum darf nicht vor dem Startdatum liegen.'));
                this.error.add('end', this.$t('Das Enddatum darf nicht vor dem Startdatum liegen.'));
                return;
            } else {
                this.error.clear('start');
                this.error.clear('end');
            }

            this.alreadyLoaded = true;
            this.loadButtonDisabled = true;
            this.lastFilter = JSON.stringify(this.filter);
            this.loading = true;
            ClubInvoiceRepository.getCountAndInvoicesByClubIdAndFilters(this.clubId, this.query)
                .then(response => {
                    this.updatePaginationLength(response[0]);
                    this.invoices = response[1];
                    if (setJson) {
                        this.invoices.forEach((invoice, index) => {
                            this.reloadInvoiceJson[index] = JSON.stringify(invoice);
                        });
                    }
                })
                .finally(() => {
                    this.loading = false;
                    this.loadButtonDisabled = false;
                });
        },
        loadOpenItems() {
            this.openItems = new OpenItems();
            this.loadingOpenItems = true;
            ClubInvoiceRepository.getOpenItemsByClubId(this.clubId, this.query)
                .then(response => { this.openItems = response })
                .finally(() => { this.loadingOpenItems = false });
        },
        reloadInvoicesAndOpenItems() {
            this.loadInvoices();
            this.loadOpenItems();
        },
        reloadInvoiceAndOpenItems(index) {
            this.reloadInvoice(index);
            this.loadOpenItems();
        },
        reloadInvoiceAndOpenItemsAndSchedulePolling(invoice, index) {
            this.reloadInvoice(index);
            this.loadOpenItems();
            this.scheduleSinglePolling(invoice.id, index);
        },
        reloadInvoice(index) {
            ClubInvoiceRepository.find(this.invoices[index].id)
                .then(clubInvoice => {
                    this.reloadInvoiceJson[index] = JSON.stringify(clubInvoice);
                    this.invoices[index] = clubInvoice;
                    this.$forceUpdate();
                });
        },
        scheduleSinglePolling(invoiceId, index) {
            if (!this.reloadInvoiceCount.hasOwnProperty(index)) {
                this.reloadInvoiceCount[index] = 0;
            }
            if (this.reloadInvoiceCount[index] < MAX_POLLING) {
                setTimeout(() => {
                    this.pollInvoice(invoiceId, index);
                    this.reloadInvoiceCount[index]++;
                }, POLLING_INTERVAL_IN_SECONDS * 1000);
            }
        },
        pollInvoice(invoiceId, index) {
            ClubInvoiceRepository.find(invoiceId)
                .then(clubInvoice => {
                    if (!this.reloadInvoiceJson.hasOwnProperty(index)
                        || null === this.reloadInvoiceJson[index]
                        || this.reloadInvoiceJson[index] === JSON.stringify(clubInvoice)
                    ) {
                        this.scheduleSinglePolling(invoiceId, index);
                    } else {
                        this.reloadInvoiceCount[index] = 0;
                        this.reloadInvoiceJson[index] = null;
                        this.invoices[index] = clubInvoice;
                        this.$forceUpdate();
                    }
                });
        },
        schedulePolling(invoiceIds) {
            if (!this.reloadInvoiceCount.hasOwnProperty(DEFAULT_POLLING_INDEX)) {
                this.reloadInvoiceCount[DEFAULT_POLLING_INDEX] = 0;
            }
            if (this.reloadInvoiceCount[DEFAULT_POLLING_INDEX] < MAX_POLLING) {
                setTimeout(() => {
                    this.pollInvoices(invoiceIds);
                    this.reloadInvoiceCount[DEFAULT_POLLING_INDEX]++;
                }, POLLING_INTERVAL_IN_SECONDS * 1000);
            }
        },
        pollInvoices(invoiceIds) {
            invoiceIds.forEach(invoiceId => {
                this.invoices.filter((invoice, index) => {
                    if (invoice.id === invoiceId) {
                        const invoiceJson = JSON.stringify(invoice);
                        if (!this.reloadInvoiceJson.hasOwnProperty(index)
                            || this.reloadInvoiceJson[index] === null
                            || this.reloadInvoiceJson[index] === invoiceJson
                        ) {
                            this.reloadInvoiceJson[index] = JSON.stringify(invoice);
                        }
                    }
                });
            });
            ClubInvoiceRepository.getInvoicesByClubId(this.clubId, this.query)
                .then(invoices => {
                    let pollAgain = false;
                    invoices.forEach((invoice, index) => {
                        if (invoiceIds.includes(invoice.id)) {
                            if (!this.reloadInvoiceJson.hasOwnProperty(index)
                                || null === this.reloadInvoiceJson[index]
                                || this.reloadInvoiceJson[index] === JSON.stringify(invoice)
                            ) {
                                pollAgain = true;
                            }
                        }
                    });
                    if (pollAgain) {
                        this.schedulePolling(invoiceIds);
                    } else {
                        this.invoices = invoices;
                        this.reloadInvoiceCount[DEFAULT_POLLING_INDEX] = 0;
                    }
                });
        },
        getServicePeriod(servicePeriod) {
            return this.helper.formatDate(servicePeriod.start) + ' - ' + this.helper.formatDate(servicePeriod.end);
        },
        getFactor(factor) {
            return (factor.numerator / factor.denominator).toFixed(2);
        },
        getPrice(price) {
            return this.helper.getPrice(price);
        },
        getMoney(money) {
            let amount = (money.amount.numerator / money.amount.denominator).toFixed(2);
            return amount + ' ' + money.currency;
        },
        isInvoiceSelectionDisabled(invoice) {
            switch (this.bulkOperation?.value) {
                case BULK_DELETE:
                    return !invoice.deletable || invoice.isReverseInvoice;
                case BULK_DELETE_REVERSE:
                    return !invoice.deletable || !invoice.isReverseInvoice;
                case BULK_MARK_PAID:
                    return invoice.isReverseInvoice || invoice.status.status === ClubInvoice.STATUS_PAID;
                case BULK_CANCEL_INVOICE:
                    return invoice.isReverseInvoice || invoice.canceled;
                case BULK_DOWNLOAD_INVOICES:
                case BULK_DOWNLOAD_COLLECTED_INVOICES:
                    return !invoice.pdfExists;
                case BULK_SEND_INVOICE_AS_MAIL:
                    return !invoice.pdfExists || invoice.invoiceRecipient.email === null;
                case BULK_INCREASE_DUNNING_STATUS:
                    return this.dunningDisabled
                        || this.dunningChecker.isBulkInvoiceSelectionDisabled(invoice);
                case BULK_SEND_DUNNING_PDFS_AS_MAIL:
                    return this.dunningDisabled
                        || invoice.isReverseInvoice
                        || invoice.invoiceRecipient.email === null
                        || invoice.dunningPdfs[invoice.dunningStatus.status] === undefined
                        || !invoice.dunningPdfs[invoice.dunningStatus.status]['pdfExists']
                        || invoice.status.status === ClubInvoice.STATUS_PAID;
                case BULK_DOWNLOAD_DUNNING_PDFS:
                case BULK_DOWNLOAD_COLLECTED_DUNNING_PDFS:
                    return this.dunningDisabled
                        || invoice.isReverseInvoice
                        || invoice.dunningPdfs[invoice.dunningStatus.status] === undefined
                        || !invoice.dunningPdfs[invoice.dunningStatus.status]['pdfExists']
                        || invoice.status.status === ClubInvoice.STATUS_PAID;
                case BULK_SEND_CHARGEBACK_PDFS_AS_MAIL:
                    return invoice.isReverseInvoice || invoice.invoiceRecipient.email === null || invoice.chargeback === null || !invoice.chargeback.pdfExists;
                case BULK_DOWNLOAD_CHARGEBACK_PDFS:
                case BULK_DOWNLOAD_COLLECTED_CHARGEBACK_PDFS:
                    return invoice.isReverseInvoice || invoice.chargeback === null || !invoice.chargeback.pdfExists;
            }
        },
        sendMail(invoice, index) {
            if (invoice.isReverseInvoice) {
                ClubInvoicePdfRepository.createClubReverseInvoicePdfEmailHistory(invoice.id)
                    .then(() => {
                        this.reloadInvoice(index);
                        this.scheduleSinglePolling(invoice.canceledInvoiceId, index);
                    });
            } else {
                ClubInvoicePdfRepository.createClubInvoicePdfEmailHistory(invoice.id)
                    .then(() => {
                        this.reloadInvoice(index);
                        this.scheduleSinglePolling(invoice.id, index);
                    });
            }
            this.closeEmailHintDialog();
        },
        sendDunningMail(invoice, index, level) {
            this.invoiceSelectedForDunningEmail = invoice;
            this.invoiceIndexSelectedForDunningEmail = index;
            this.dunningLevelSelectedForEmail = level;
            this.showDunningEmailHintDialog = true;
        },
        confirmSendDunningMail() {
            const invoice = this.invoiceSelectedForDunningEmail;
            const index = this.invoiceIndexSelectedForDunningEmail;
            const level = this.dunningLevelSelectedForEmail;

            ClubInvoiceDunningPdfRepository.createEmailHistory(invoice.id, level)
                .then(() => {
                    this.reloadInvoice(index);
                    this.scheduleSinglePolling(invoice.id, index);
                });

            this.closeDunningEmailHintDialog();
        },
        closeDunningEmailHintDialog() {
            this.showDunningEmailHintDialog = false;
            this.invoiceSelectedForDunningEmail = null;
            this.invoiceIndexSelectedForDunningEmail = null;
            this.dunningLevelSelectedForEmail = null;
        },
        sendChargebackMail(invoice, index) {
            ClubInvoiceChargebackPdfRepository.createEmailHistory(invoice.id)
                .then(() => {
                    this.reloadInvoice(index);
                    this.scheduleSinglePolling(invoice.id, index);
                });
        },
        updateBulkSelection(selectionValue) {
            Vue.set(this.bulkSelection, selectionValue, !this.bulkSelection[selectionValue]);
        },
        executeBulkOperation() {
            switch (this.bulkOperation?.value) {
                case BULK_DELETE:
                    return this.bulkDelete();
                case BULK_DELETE_REVERSE:
                    return this.bulkDeleteReverse();
                case BULK_MARK_PAID:
                    this.openMarkPaidDialog();
                    return;
                case BULK_CANCEL_INVOICE:
                    return this.bulkCancelInvoices();
                case BULK_DOWNLOAD_INVOICES:
                    return this.bulkDownloadInvoices(false);
                case BULK_DOWNLOAD_COLLECTED_INVOICES:
                    return this.bulkDownloadInvoices(true);
                case BULK_SEND_INVOICE_AS_MAIL:
                    return this.bulkOpenEmailsDialog();
                case BULK_INCREASE_DUNNING_STATUS:
                    return this.bulkIncreaseDunningStatus();
                case BULK_SEND_DUNNING_PDFS_AS_MAIL:
                    return this.bulkSendDunningPdfsAsEmail();
                case BULK_DOWNLOAD_DUNNING_PDFS:
                    return this.bulkDownloadInvoiceDunningPdfs(false);
                case BULK_DOWNLOAD_COLLECTED_DUNNING_PDFS:
                    return this.bulkDownloadInvoiceDunningPdfs(true);
                case BULK_SEND_CHARGEBACK_PDFS_AS_MAIL:
                    return this.bulkSendChargebackPdfsAsEmail();
                case BULK_DOWNLOAD_CHARGEBACK_PDFS:
                    return this.bulkDownloadInvoiceChargebackPdfs(false);
                case BULK_DOWNLOAD_COLLECTED_CHARGEBACK_PDFS:
                    return this.bulkDownloadInvoiceChargebackPdfs(true);
            }
            throw new Error(this.$t('Unbekannte Stapelverarbeitungsoption') + ' "' + this.bulkOperation + '"');
        },
        bulkDelete() {
            let invoiceIds = this.getSelectedIds;
            if (invoiceIds.length === 0) {
                return;
            }
            this.loading = true;
            axios
                .delete('/club/clubs/' + this.clubId + '/invoices', { data: { invoices: invoiceIds } })
                .then(() => this.reloadInvoicesAndOpenItems())
                .catch(() => this.loading = false);
            this.clear();
        },
        bulkDeleteReverse() {
            let invoiceIds = this.getSelectedIds;
            if (invoiceIds.length === 0) {
                return;
            }
            this.loading = true;
            axios
                .delete('/club/clubs/' + this.clubId + '/reverse-invoices', { data: { reverseInvoices: invoiceIds } })
                .then(() => this.reloadInvoicesAndOpenItems())
                .catch(() => this.loading = false);
            this.clear();
        },
        bulkMarkAs(status) {
            const selectedIds = this.getSelectedIds;
            const invoices = this.invoices.filter(invoice => selectedIds.includes(invoice.id)).map(invoice => invoice.id);
            if (invoices.length === 0) {
                return;
            }
            const date = parse(this.bulkAppliedAt, 'dd.MM.yyyy', new Date());
            const error = new Error();
            if (isAfter(date, endOfToday())) {
                error.add('bulkAppliedAt', this.$t('Das Datum darf nicht in der Zukunft liegen.'));
            } else {
                error.clear('bulkAppliedAt');
            }
            this.error = error;
            if (!this.error.isEmpty()) {
                return;
            }
            const data = {
                invoices: invoices,
                status: {
                    status: status,
                    appliedAt: { day: date.getDate(), month: date.getMonth() + 1, year: date.getFullYear() }
                },
                clubFeeId: null !== this.clubDunningSettings.clubFeeForChargeback ? this.clubDunningSettings.clubFeeForChargeback.id : null,
            }
            this.loading = true;
            ClubInvoiceRepository.bulkUpdateStatus(this.clubId, data)
                .then(() => {
                    this.closeMarkPaidDialog();
                    this.reloadInvoicesAndOpenItems();
                })
                .catch(() => this.$notify.error('saveChanges.error'))
                .finally(() => this.loading = false)
            this.clear();
        },
        bulkCancelInvoices() {
            let invoiceIds = this.getSelectedIds;
            if (invoiceIds.length === 0) {
                return;
            }
            this.loading = true;
            ClubInvoiceRepository.cancelByClubIdAndInvoiceIds(this.clubId, invoiceIds)
                .then(() => {
                    this.loadInvoices(true);
                    this.loadOpenItems();
                    this.schedulePolling(invoiceIds);
                })
                .catch(() => this.$notify.error('saveChanges.error'))
                .finally(() => this.loading = false);
            this.clear();
        },
        bulkOpenEmailsDialog() {
            this.bulkInvoicesIndexSelected = this.getSelectedIds;
            this.bulkInvoicesSelected = this.invoices.filter(invoice => this.bulkInvoicesIndexSelected.includes(invoice.id));
            const reverseInvoiceIds = this.getSelectedReverseInvoiceIds;
            const fileCount = this.bulkInvoicesSelected.length + reverseInvoiceIds.length;
            if (fileCount === 0) {
                return;
            }
            this.showBulkEmailHintDialog = true;
        },
        bulkSendInvoiceAsEmail() {
            const invoiceIds = this.bulkInvoicesSelected.map(invoice => invoice.id);
            const reverseInvoiceIds = this.getSelectedReverseInvoiceIds;
            ClubInvoicePdfRepository.createBulkPdfEmailHistories(this.clubId, invoiceIds, reverseInvoiceIds)
                .then(() => {
                    this.loadInvoices(true);
                    this.schedulePolling(invoiceIds);
                })
                .catch(() => this.$notify.error('saveChanges.error'));
            this.closeBulkEmailsHintDialog();
            this.clear();
            this.bulkAppliedAt = this.todayAsString;
        },
        bulkDownloadInvoices(collected) {
            let selectedIds = this.getSelectedIds;
            let invoiceIds = this.invoices.filter(invoice => selectedIds.includes(invoice.id)).map(invoice => invoice.id);
            let reverseInvoiceIds = this.getSelectedReverseInvoiceIds;
            let fileCount = invoiceIds.length + reverseInvoiceIds.length;
            if (fileCount === 0) {
                return;
            }
            if (fileCount > 100) {
                this.openFileCountDialog();
                return;
            }
            const url = '/club/clubs/' + this.clubId + '/club-invoice-pdfs/download-history';
            axios.post(url, { invoices: invoiceIds, reverseInvoices: reverseInvoiceIds, collected: collected })
                .then(response => {
                    this._downloadCollected(response.headers.location)
                    this.loadInvoices();
                    this.clear();
                });
        },
        bulkIncreaseDunningStatus() {
            const invoiceIds = this.getSelectedIds;
            ClubInvoiceRepository.bulkIncreaseDunningStatus(invoiceIds)
                .then(() => {
                    this.loadInvoices(true);
                    this.loadOpenItems();
                    this.schedulePolling(invoiceIds);
                    this.clear();
                });
        },
        bulkSendDunningPdfsAsEmail() {
            this.bulkInvoicesIndexSelected = this.getSelectedIds;
            this.bulkInvoicesSelected = this.invoices.filter(invoice => this.bulkInvoicesIndexSelected.includes(invoice.id));
            if (this.bulkInvoicesSelected.length === 0) {
                return;
            }
            this.showBulkDunningEmailHintDialog = true;
        },
        confirmBulkSendDunningPdfsAsEmail() {
            const invoiceIds = this.bulkInvoicesSelected.map(invoice => invoice.id);
            ClubInvoiceDunningPdfRepository.bulkCreateEmailHistory(invoiceIds)
                .then(() => {
                    this.loadInvoices(true);
                    this.schedulePolling(invoiceIds);
                })
                .catch(() => this.$notify.error('saveChanges.error'));
            this.closeBulkDunningEmailsHintDialog();
            this.clear();
        },
        bulkDownloadInvoiceDunningPdfs(collected) {
            ClubInvoiceDunningPdfRepository.bulkCreateDownloadHistory(this.getSelectedIds, collected)
                .then(response => {
                    this._downloadCollected(response.headers.location)
                    this.loadInvoices();
                    this.clear();
                });
        },
        bulkDownloadInvoiceChargebackPdfs(collected) {
            ClubInvoiceChargebackPdfRepository.bulkCreateDownloadHistory(this.clubId, this.getSelectedIds, collected)
                .then(response => {
                    this._downloadCollected(response.headers.location)
                    this.loadInvoices();
                    this.clear();
                });
        },
        bulkSendChargebackPdfsAsEmail() {
            ClubInvoiceChargebackPdfRepository.bulkCreateEmailHistory(this.clubId, this.getSelectedIds)
                .then(() => {
                    this.loadInvoices();
                    this.clear();
                });
        },
        _downloadCollected(location) {
            axios.get(location, { responseType: 'blob' })
                .then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    let filename = this.helper.getFilenameFromHeaders(response.headers);
                    link.setAttribute('download', filename);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                });
        },
        formatDate(date) {
            return this.helper.formatDate(date);
        },
        translatePaymentMethod(paymentMethod) {
            return this.helper.translatePaymentMethod(paymentMethod);
        },
        translateInvoiceAmount(invoiceAmount) {
            return this.helper.translateInvoiceAmount(invoiceAmount);
        },
        overallMoney(invoice) {
            let amount = (invoice.gross.data.numerator / invoice.gross.data.denominator);

            return amount.toFixed(2).replace('.', ',') + ' ' + invoice.currency;
        },
        updatePaginationLength(totalCount) {
            this.totalInvoiceCount = totalCount;
            this.pageCount = Math.ceil(totalCount / this.pagination.limit);
        },
        selectAll() {
            const selected = this.checkboxSelectAll;
            this.bulkSelection = [];

            if (selected) {
                this.invoices.forEach(invoice => {
                    if (!this.isInvoiceSelectionDisabled(invoice)) {
                        this.bulkSelection.push(invoice.id);
                    }
                    if (invoice.reverseInvoice && !this.isInvoiceSelectionDisabled(invoice.reverseInvoice)) {
                        this.bulkSelection.push(invoice.reverseInvoice.id);
                    }
                });
            }
        },

        clear() {
            this.bulkSelection = [];
            this.checkboxSelectAll = false;
        },

        isSelected(invoiceId) {
            return this.bulkSelection.includes(invoiceId);
        },

        toggleInvoice(invoiceId) {
            const index = this.bulkSelection.indexOf(invoiceId);
            if (index === -1) {
                this.bulkSelection.push(invoiceId);
            } else {
                this.bulkSelection.splice(index, 1);
            }
            this.updateCheckboxSelectAll();
        },

        updateCheckboxSelectAll() {
            const selectableInvoices = this.invoices.filter(invoice => !this.isInvoiceSelectionDisabled(invoice));
            const selectableIds = selectableInvoices.map(invoice => invoice.id);

            this.checkboxSelectAll = selectableIds.every(id => this.bulkSelection.includes(id));
        },
        resetSelectAll() {
            this.checkboxSelectAll = false;
            this.bulkSelection = [];
        },
        getDeleteInvoiceButtonTitle(invoice) {
            return this.helper.getDeleteInvoiceButtonTitle(invoice);
        },
        loadClubDunningSettings() {
            ClubDunningSettingsRepository.findByClubId(this.clubId)
                .then(response => this.clubDunningSettings = response);
        },
        translateDunningFilter(level) {
            return this.$t(this.helper.translateDunningStatus(level));
        },
        toggleInvoiceStatus(status) {
            const index = this.filter.invoiceStatus.indexOf(status);
            if (index === -1) {
                this.filter.invoiceStatus.push(status);
            } else {
                this.filter.invoiceStatus.splice(index, 1);
            }
        },
        toggleInvoicePaymentMethod(method) {
            const index = this.filter.invoicePaymentMethod.indexOf(method);
            if (index === -1) {
                this.filter.invoicePaymentMethod.push(method);
            } else {
                this.filter.invoicePaymentMethod.splice(index, 1);
            }
        },
        toggleInvoiceAmount(amount) {
            const index = this.filter.invoiceAmount.indexOf(amount);
            if (index === -1) {
                this.filter.invoiceAmount.push(amount);
            } else {
                this.filter.invoiceAmount.splice(index, 1);
            }
        },
        toggleOthers(other) {
            const index = this.filter.others.indexOf(other);
            if (index === -1) {
                this.filter.others.push(other);
            } else {
                this.filter.others.splice(index, 1);
            }
        },
        toggleDunningLevel(level) {
            const index = this.filter.dunningLevel.indexOf(level);
            if (index === -1) {
                this.filter.dunningLevel.push(level);
            } else {
                this.filter.dunningLevel.splice(index, 1);
            }
        },
        toggleDunningStatus(status) {
            const index = this.filter.dunningStatus.indexOf(status);
            if (index === -1) {
                this.filter.dunningStatus.push(status);
            } else {
                this.filter.dunningStatus.splice(index, 1);
            }
        },
        openMarkPaidDialog() {
            this.showMarkPaidDialog = true;
        },
        closeMarkPaidDialog() {
            this.showMarkPaidDialog = false;
        },
        openFileCountDialog() {
            this.showFileCountDialog = true;
        },
        closeFileCountDialog() {
            this.showFileCountDialog = false;
        },
        openEmailHintDialog(invoice, index) {
            this.invoiceSelectedForEmail = invoice;
            this.invoiceIndexSelectedForEmail = index;
            this.showEmailHintDialog = true;
        },
        closeEmailHintDialog() {
            this.showEmailHintDialog = false;
            this.invoiceSelectedForEmail = null;
            this.invoiceIndexSelectedForEmail = null;
        },
        closeBulkEmailsHintDialog() {
            this.showBulkEmailHintDialog = false;
            this.bulkInvoicesSelected = [];
            this.bulkInvoicesIndexSelected = [];
        },
        closeBulkDunningEmailsHintDialog() {
            this.showBulkDunningEmailHintDialog = false;
            this.bulkInvoicesSelected = [];
            this.bulkInvoicesIndexSelected = [];
        },
        getRowClass(index) {
            return index % 2 === 0 ? 'is-striped' : '';
        }
    },
}
</script>

<style scoped lang="scss" rel="stylesheet/scss">
#loadButton {
    margin: 10px 20px;
    float: right;
}
</style>
